import React, { useEffect, useState } from 'react';
import { Camera, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import { apiService } from '../../../services/ApiService';

/**
 * Admin + debug mode: what the vision agent read from the photo and what the
 * search did with it, inline under the marked photo — no click needed.
 *
 * Same data as the header's Vision panel (GET /api/chat/vision-extractions),
 * shown as a readable card: per part the name, condition, side/position,
 * confidence, the catalog names vision gave it, its description and notes,
 * the four review indications (missing evidence, shape, size with its scale
 * reference, adjacency anchors) and any item the code dropped, with why;
 * then the search's use of it (typed reading, photo-observed axes, the
 * question the photo designed and its suggested answer). The header panel
 * keeps the full raw JSON.
 */

type Dict = Record<string, any>;

const Chip: React.FC<{ children: React.ReactNode; tone?: 'default' | 'good' }> = ({ children, tone = 'default' }) => (
  <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border mr-1 mb-1 ${
    tone === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
    {children}
  </span>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex gap-2 text-[12px] leading-snug">
    <span className="shrink-0 w-28 text-slate-500">{label}</span>
    <span className="min-w-0 text-slate-800 break-words">{children}</span>
  </div>
);

/** "side: left · placement: outer" — every key the reading carries, in order. */
const axesLine = (axes?: Dict | null) =>
  axes && typeof axes === 'object'
    ? Object.entries(axes).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `${k}: ${v}`).join(' · ')
    : '';

/** "620–700 mm" / "≥ 900 mm" / "" for one DimensionRange. */
export const rangeText = (r?: Dict | null) => {
  if (!r || typeof r !== 'object') return '';
  const { min_mm: lo, max_mm: hi, partial } = r;
  if (lo == null && hi == null) return '';
  if (partial) return `≥ ${lo ?? hi} mm`;
  if (lo != null && hi != null && lo !== hi) return `${lo}–${hi} mm`;
  return `${lo ?? hi} mm`;
};

/** Shape line: form · profile (bends) · cross-section · ends · feature counts · handedness · orientation. */
export const shapeLine = (g?: Dict | null) => {
  if (!g || typeof g !== 'object') return '';
  const bits: string[] = [];
  if (g.overall_form) bits.push(g.overall_form);
  if (g.profile) bits.push(g.bend_count != null ? `${g.profile} (${g.bend_count} bends)` : g.profile);
  if (g.cross_section) bits.push(g.cross_section);
  if (Array.isArray(g.ends) && g.ends.length) bits.push(`ends: ${g.ends.map((e: Dict) => `${e.end} ${e.kind}`).join(', ')}`);
  if (Array.isArray(g.surface_features) && g.surface_features.length) {
    bits.push(g.surface_features.map((f: Dict) => `${f.partial ? '≥' : ''}${f.count} ${f.kind}`).join(', '));
  }
  if (g.handed != null) bits.push(g.handed ? `handed${g.handed_reason ? ` (${g.handed_reason})` : ''}` : 'symmetric');
  if (g.orientation) bits.push(g.orientation);
  return bits.join(' · ');
};

/** Size line: L/W/H ranges, ratio, scale reference, method, confidence, angled shot. */
export const sizeLine = (d?: Dict | null) => {
  if (!d || typeof d !== 'object') return '';
  const bits: string[] = [];
  (['length', 'width', 'height'] as const).forEach(k => { const t = rangeText(d[k]); if (t) bits.push(`${k[0].toUpperCase()} ${t}`); });
  if (d.width_to_height_ratio != null) bits.push(`ratio ${d.width_to_height_ratio}:1`);
  const ref = [d.scale_reference, d.method].filter(Boolean).join(', ');
  if (ref) bits.push(`scale: ${ref}`);
  if (typeof d.confidence === 'number') bits.push(`${Math.round(d.confidence * 100)}%`);
  if (d.angled_shot) bits.push('angled shot');
  return bits.join(' · ');
};

/** "mounted_on: Rear bumper reinforcement" / "position (below): Tailgate". */
export const anchorLabel = (a: Dict) =>
  `${a.relation}${a.relation === 'position' && a.direction ? ` (${a.direction})` : ''}: ${a.name}`;

/** Seconds since the epoch of a message timestamp (Date | ISO string | ms | s). */
export const toSeconds = (t: unknown): number | null => {
  if (t instanceof Date) return t.getTime() / 1000;
  if (typeof t === 'number') return t > 1e12 ? t / 1000 : t;
  if (typeof t === 'string') { const ms = Date.parse(t); return Number.isNaN(ms) ? null : ms / 1000; }
  return null;
};

const hasItems = (x: Dict) =>
  (Array.isArray(x?.cleaned?.identified_items) && x.cleaned.identified_items.length > 0)
  || (Array.isArray(x?.dropped_missing) && x.dropped_missing.length > 0);

/**
 * The vision pass THIS message shows. The log holds every pass of the
 * session; taking the last one made every earlier card show whatever a later
 * turn read (a VIN-only photo → an empty card). A card under a marked photo
 * belongs to a pass that found parts: the one that ran closest to the
 * message, never one that ran after it (2 min slack for clock skew / the
 * message being stamped before the turn finished). Falls back to the latest.
 */
export const pickExtraction = (extractions: Dict[], messageTime?: unknown): Dict => {
  const withItems = extractions.filter(hasItems);
  const t = toSeconds(messageTime);
  if (withItems.length && t != null) {
    const before = withItems.filter(x => typeof x.at === 'number' && x.at <= t + 120);
    const pool = before.length ? before : withItems;
    return pool.reduce((best, x) =>
      Math.abs((x.at ?? 0) - t) < Math.abs((best.at ?? 0) - t) ? x : best, pool[0]);
  }
  return withItems[withItems.length - 1] || extractions[extractions.length - 1] || {};
};

const VisionDebugInline: React.FC<{ refreshKey?: string | number; messageTime?: unknown }> = ({ refreshKey, messageTime }) => {
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const sessionId = useActiveSessionIdSafe();
  const [data, setData] = useState<{ extractions: Dict[]; search: Dict } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The marked photo shows before the search finishes: refresh pulls the
  // search's use of the photo once it has run.
  const [tick, setTick] = useState(0);
  const enabled = currentUser?.role === 'admin' && debugMode && !!sessionId;

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    apiService.get(`/api/chat/vision-extractions?session_id=${encodeURIComponent(sessionId as string)}`)
      .then(res => { if (alive) setData({ extractions: res.data?.extractions || [], search: res.data?.search || {} }); })
      .catch((e: any) => { if (alive) setError(e?.message || 'Failed to load vision output'); });
    return () => { alive = false; };
  }, [enabled, sessionId, refreshKey, tick]);

  if (!enabled) return null;
  if (error) return <div className="mt-2 text-[11px] text-rose-600">Vision debug: {error}</div>;
  if (!data) return null;

  const last = pickExtraction(data.extractions, messageTime);
  const items: Dict[] = Array.isArray(last.cleaned?.identified_items) ? last.cleaned.identified_items : [];
  const droppedMissing: Dict[] = Array.isArray(last.dropped_missing) ? last.dropped_missing : [];
  const evidenceOff = last.evidence_enabled === false;
  const search = data.search || {};
  const identity: Dict = search.identity_per_part || {};
  const reading: Dict = search.request_reading || {};
  const observed: Dict = search.photo_observed_axes || {};
  const photoQs: Dict[] = Array.isArray(search.photo_questions) ? search.photo_questions : [];
  const exactPiece: Dict = search.exact_piece || {};
  const exactMode: string = search.exact_piece_mode || '';
  const refDims: Dict = last.reference_dimensions || {};
  const refLine = [
    refDims.width_mm && `width ${refDims.width_mm} mm`, refDims.length_mm && `length ${refDims.length_mm} mm`,
    refDims.wheelbase_mm && `wheelbase ${refDims.wheelbase_mm} mm`, refDims.tyre && `tyre ${refDims.tyre}`,
    Array.isArray(refDims.plate_mm) && `plate ${refDims.plate_mm[0]}×${refDims.plate_mm[1]} mm`,
  ].filter(Boolean).join(' · ');
  if (!items.length && !droppedMissing.length && !Object.keys(search).length) return null;

  return (
    <div data-testid="vision-debug-inline" className="mt-2 rounded-xl border border-slate-200 bg-slate-50/80 text-left">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Camera className="w-3.5 h-3.5" /> Vision — what the photo showed
        <button type="button" onClick={() => setTick(t => t + 1)} title="Refresh (after the search has run)"
          className="ml-auto text-slate-400 hover:text-slate-700">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-3 py-2 space-y-3">
        {items.map((it, i) => {
          const name = String(it.part_name || it.name || `Item ${i + 1}`);
          const aliases: string[] = it.catalog_aliases || identity[name]?.aliases || [];
          return (
            <div key={i} className="space-y-1">
              <div className="text-[13px] font-semibold text-slate-900">{name}</div>
              <Row label="condition">{[it.condition, it.damage_type, it.damage_severity].filter(Boolean).join(' · ') || '—'}</Row>
              <Row label="where">{[it.part_area, it.vehicle_position].filter(Boolean).join(' · ') || '—'}</Row>
              {typeof it.confidence_score === 'number' && <Row label="confidence">{Math.round(it.confidence_score * 100)}%</Row>}
              {aliases.length > 0 && <Row label="catalog names">{aliases.map(a => <Chip key={a}>{a}</Chip>)}</Row>}
              {it.detailed_description && <Row label="description">{it.detailed_description}</Row>}
              {it.notes && <Row label="notes">{it.notes}</Row>}
              {Array.isArray(it.missing_evidence) && it.missing_evidence.length > 0 && (
                <Row label="missing evidence">{it.missing_evidence.map((m: string) => <Chip key={m}>{m}</Chip>)}</Row>
              )}
              {shapeLine(it.geometry) && <Row label="shape">{shapeLine(it.geometry)}</Row>}
              {sizeLine(it.geometry?.dimensions) && <Row label="size">{sizeLine(it.geometry?.dimensions)}</Row>}
              {Array.isArray(it.adjacency) && it.adjacency.length > 0 && (
                <Row label="anchors">{it.adjacency.map((a: Dict, j: number) => (
                  <Chip key={j}>{anchorLabel(a)}{typeof a.confidence === 'number' ? ` · ${Math.round(a.confidence * 100)}%` : ''}</Chip>
                ))}</Row>
              )}
            </div>
          );
        })}
        {droppedMissing.map((d, i) => (
          <Row key={`dropped-${i}`} label="dropped">
            <span className="text-amber-700">{d.part_name} (missing) — {d.reason}</span>
          </Row>
        ))}
        {refLine && <Row label="scale given">{refLine}</Row>}
        {evidenceOff && (
          <div className="text-[11px] text-slate-500">Shape, size and anchors are off for this pass (VISION_EVIDENCE=false).</div>
        )}
      </div>
      {Object.keys(exactPiece).length > 0 && (
        <>
          <div className="flex items-center gap-1.5 px-3 py-2 border-y border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <Search className="w-3.5 h-3.5" /> Pick the exact piece{exactMode ? ` (${exactMode})` : ''}
          </div>
          <div className="px-3 py-2 space-y-3" data-testid="exact-piece-debug">
            {Object.entries(exactPiece).map(([term, v]: [string, any]) => (
              <div key={term} className="space-y-1">
                <div className="text-[13px] font-semibold text-slate-900">{term}</div>
                <Row label="steps">{(v.steps || []).map((st: Dict) => `${st.step} ${st.before}→${st.after}`).join(' · ') || '—'}</Row>
                {(v.ruled_out || []).map((r: Dict, i: number) => (
                  <Row key={i} label="ruled out">
                    <span className="text-rose-700">{r.part_name || r.partId}</span> — {r.reason} <span className="text-slate-400">({r.step})</span>
                  </Row>
                ))}
                {Object.entries(v.candidates || {}).filter(([, c]: [string, any]) => !c.removed && c.multiplier < 1).map(([pid, c]: [string, any]) => (
                  <Row key={pid} label="lowered">
                    {c.part_name || pid} ×{c.multiplier} — {(c.reasons || []).filter((x: Dict) => !x.hard).map((x: Dict) => x.reason).join('; ')}
                  </Row>
                ))}
                <Row label="remaining">{(v.remaining || []).map((pid: string) => v.candidates?.[pid]?.part_name || pid).join(', ') || '—'}</Row>
                {v.deciding_difference && <Row label="difference">{v.deciding_difference}</Row>}
                {v.cannot_separate && <Row label="outcome"><Chip>cannot_separate → staff review</Chip></Row>}
              </div>
            ))}
          </div>
        </>
      )}
      {(Object.keys(reading).length > 0 || Object.keys(observed).length > 0 || photoQs.length > 0) && (
        <>
          <div className="flex items-center gap-1.5 px-3 py-2 border-y border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <Search className="w-3.5 h-3.5" /> What the search used
          </div>
          <div className="px-3 py-2 space-y-3">
            {Object.entries(reading).map(([line, axes]) => (
              <div key={line} className="space-y-1">
                <div className="text-[13px] font-semibold text-slate-900">{line}</div>
                <Row label="read as">{axesLine(axes as Dict) || '—'}</Row>
                {observed[line] && <Row label="photo narrows"><Chip tone="good">{axesLine(observed[line])}</Chip></Row>}
              </div>
            ))}
            {photoQs.map(q => (
              <div key={q.question_id} className="space-y-1">
                <Row label="photo question">{q.text}</Row>
                {Array.isArray(q.choices) && <Row label="choices">{q.choices.map((c: string) =>
                  <Chip key={c} tone={c === q.suggested ? 'good' : 'default'}>{c}{c === q.suggested ? ' ✓ suggested' : ''}</Chip>)}</Row>}
                {q.photo_shows && <Row label="photo shows">{q.photo_shows}</Row>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VisionDebugInline;
